import { queryKeyFactory } from '@wyz/utils';
import { CommonQueryKeys } from '~/common/api';
import { IJob } from '~/common/types';

export const users = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 'users'],
  detail: function (id: string) {
    return [...this.base(), id];
  },
  customersByUser: function (
    id: string,
    minLevel: number,
    parentIds?: string[],
  ) {
    return [...this.base(), id, 'customers', minLevel, ...(parentIds ?? [])];
  },
  containerEligibleCustomersByUser: function (
    id: string,
    minLevel: number,
    parentIds?: string[],
  ) {
    return [
      ...this.base(),
      id,
      'customers',
      'container-eligible',
      minLevel,
      ...(parentIds ?? []),
    ];
  },
  customerByUser: function (userId: string, customerId: string) {
    return [...this.base(), userId, 'customer', customerId];
  },
  paginateCustomersByUser: function (userId: string, customerIds: string[]) {
    return [...this.base(), userId, 'customers', ...(customerIds ?? [])];
  },
  listSupplierByUser: function (userId: string) {
    return [...this.base(), userId, 'suppliers'];
  },
  containerOrderListByUser: function (userId: string) {
    return [...this.base(), userId, 'container-orders'];
  },
  containerOrderDistinctCities: function (userId: string) {
    return [...this.base(), userId, 'container-orders', 'cities'];
  },
  containerOrderDistinctCountries: function (userId: string) {
    return [...this.base(), userId, 'container-orders', 'countries'];
  },
});

export const adverts = queryKeyFactory({
  base: (tenantCode: string) => [
    ...CommonQueryKeys.root(),
    'adverts',
    tenantCode,
  ],
});

export const odrOperations = queryKeyFactory({
  base: (tenantCode: string) => [
    ...CommonQueryKeys.root(),
    'odr-operations',
    tenantCode,
  ],
  detail: function (tenantCode: string, id: string | undefined) {
    return [...this.base(tenantCode), 'detail', id];
  },
});

export const languages = queryKeyFactory({
  base: (filter: unknown) => [...CommonQueryKeys.root(), 'languages', filter],
  activeByTenant: () => [...CommonQueryKeys.root(), 'tenant-languages'],
});

export const parameters = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 'parameters'],
  referenceGroups: function () {
    return [...this.base(), 'reference-groups'];
  },
  priceFrameTypes: function () {
    return [...this.base(), 'price-frame-types'];
  },
});

export const automakerGroups = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 'automaker-groups'],
  paginate: function () {
    return [...this.base(), 'paginate'];
  },
  all: function () {
    return [...this.base(), 'all'];
  },
});

export const changeLogs = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 'change-logs'],
  paginate: function (collection) {
    return [...this.base(), 'paginate', collection];
  },
});
export const connectionLogs = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 'connection-logs'],
  paginate: function (userId) {
    return [...this.base(), 'paginate', userId];
  },
});
export const jobs = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 'jobs'],
  list: function (type: IJob['type']) {
    return [...this.base(), type];
  },
});

export const countries = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 'countries'],
  active: function () {
    return [...this.base(), 'active'];
  },
  detail: function (id: string) {
    return [...this.base(), id];
  },
});

export const criteria = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 'criteria'],
  all: function (query?: unknown) {
    return [...this.base(), 'all', query];
  },
  byCode: function (code: string) {
    return [...this.base(), code];
  },
  checkUsage: function (id: string) {
    return [...this.base(), id, 'check-usage'];
  },
});

export const ecotaxesZones = queryKeyFactory({
  base: (countryId: number | undefined) => ['ecotaxes_zones', countryId],
  detail: function (countryId: number | undefined, id: string) {
    return [...this.base(countryId), id];
  },
});

export const customers = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 'customers'],
  parents: function (id?: string) {
    return [...this.base(), 'parents', id];
  },
  isInitialized: function (tenantCode: string) {
    return [...this.base(), 'is_initialized', tenantCode];
  },
  suppliers: function (id?: string) {
    return [...this.base(), 'suppliers', id];
  },
  levels: function () {
    return [...this.base(), 'levels'];
  },
  types: function () {
    return [...this.base(), 'types'];
  },
  adverts: function (customerId: string) {
    return [...this.base(), customerId, 'adverts'];
  },
  brands: function (customerId: string, ctx: string) {
    return [...this.base(), customerId, ctx, 'brands'];
  },
  detail: function (id: string) {
    return [...this.base(), 'detail', id];
  },
  matchedByCriteria: function (query: unknown) {
    return [...this.base(), 'matches', query];
  },
  children: function (id: string) {
    return [...this.base(), 'children', id];
  },
  getEligibleForContainerOrders: function () {
    return [...this.base(), 'eligible-for-container-orders'];
  },
});

export const supplierTemplates = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 'supplier-templates'],
});
export const wheelSupplierTemplates = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 'wheel-supplier-templates'],
});
export const redistributionSupplierTemplates = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 'redistribution-supplier-templates'],
  match: function (criteria: unknown) {
    return [...this.base(), 'matches', criteria];
  },
});

export const redistributionGenericRule = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 'redistribution-generic-rule'],
});

export const settingTemplates = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 'setting-templates'],
  match: function (criteria: unknown) {
    return [...this.base(), 'matches', criteria];
  },
});

export const suppliers = queryKeyFactory({
  base: () => ['suppliers'],
});

export const customerGroups = queryKeyFactory({
  base: () => ['customerGroups'],
});

export const vatZones = queryKeyFactory({
  base: (countryId: number | undefined) => ['vat-zones', countryId],
});

export const vehicleTypeGroups = queryKeyFactory({
  base: (tenant: string) => ['vehicle-types-groups', tenant],
  core: () => ['core-vehicle-types-groups'],
});

export const invoicingCompany = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 'invoicing-company'],
});

export const brandTemplate = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 'brand-templates'],
  match: (criteria: unknown) => ['brand-templates.match', criteria],
});

export const quoteBrandTemplate = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 'quote-brand-templates'],
  match: (criteria: unknown) => ['quote-brand-templates.match', criteria],
});

export const redistributionBrandTemplate = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 'redistribution-brand-templates'],
  match: (criteria: unknown) => [
    'redistribution-brand-templates.match',
    criteria,
  ],
});

export const s2sFlowTemplates = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 's2s-flow-templates'],
  match: function (criteria: unknown) {
    return [...this.base(), 'matches', criteria];
  },
});

export const s2sBusinessRules = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 's2s-business-rules'],
  paginate: function (query: unknown) {
    return [...this.base(), 'paginate', query];
  },
  detail: function (id: string | undefined) {
    return [...this.base(), 'detail', id];
  },
});

export const s2sFlowTypes = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 's2s-flow-types'],
});

export const s2sPartners = queryKeyFactory({
  base: (platformId: string) => [
    ...CommonQueryKeys.root(),
    's2s-partners',
    platformId,
  ],
});

export const segments = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 'segments'],
});

export const legalNotices = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 'legal-notices'],
});

export const termsOfUse = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 'terms-of-use'],
});

export const useOfCookies = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 'use-of-cookies'],
});

export const confidentialityAgreements = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 'confidentiality-agreements'],
});

export const alternativeSupplierRankingModel = queryKeyFactory({
  base: () => [...CommonQueryKeys.root(), 'alternative-supplier-ranking-model'],
});
